/**
 * Fake salon data for the new beauty/barber booking demo.
 *
 * This module fully replaces the real backend until the spec in
 * `docs/api/Backend-05-salon-booking.md` is implemented. The shape mirrors
 * what we expect the backend to return so swapping it out later is a
 * straight 1:1 replacement.
 *
 * Everything here is deterministic — slot generation seeds off the current
 * date so repeated mounts during the same day produce identical results.
 */

/* ---------------------------- SALONS ---------------------------- */

const SALONS = {
  demo: {
    slug: "demo",
    name: { hy: "Glamour Studio", en: "Glamour Studio" },
    tagline: {
      hy: "Մազահարդարման և գեղեցկության պրեմիում սրահ Երևանի կենտրոնում",
      en: "Premium hair & beauty studio in central Yerevan",
    },
    address: {
      hy: "Տերյան 23, Երևան",
      en: "23 Teryan str., Yerevan",
    },
    phone: "+374 77 123 456",
    instagram: "@glamour.studio.am",
    rating: 4.9,
    reviewsCount: 482,
    bookedToday: 37,
    repeatRate: 68,
    avgConfirmMinutes: 2,
    workingHours: {
      hy: "Ամեն օր 10:00–21:00",
      en: "Daily 10:00–21:00",
    },
    valueProps: [
      {
        icon: "las la-bolt",
        hy: "Ամենամոտ ազատ ժամերը տեսանելի են անմիջապես",
        en: "Closest free slots are visible instantly",
      },
      {
        icon: "las la-user-check",
        hy: "Համակարգը ցույց է տալիս միայն համապատասխան վարպետներին",
        en: "Only eligible specialists are shown",
      },
      {
        icon: "las la-bell",
        hy: "Հիշեցումներ և հաստատում հեռախոսով",
        en: "Phone confirmation and reminders",
      },
    ],
    coverImage:
      "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1600&h=600&fit=crop&auto=format&q=80",
    logoEmoji: "💇",
    brandColor: "hsl(166 100% 40%)",
    brandColorSoft: "hsla(166, 100%, 40%, 0.14)",
    currency: "AMD",
  },
};

/* ---------------------------- SERVICE CATEGORIES ---------------------------- */
/**
 * Each category groups its own services. `services[].priceFrom` is used when
 * the price depends on hair length / complexity; otherwise `price` is fixed.
 * `duration` is in minutes, used to compute total appointment length.
 *
 * `masterIds` is the list of masters that can perform this service. An empty
 * array means "every master can do it".
 */
const SERVICE_CATEGORIES = [
  {
    id: "haircut",
    name: { hy: "Մազահարդարում", en: "Haircuts" },
    description: {
      hy: "Արագ խուզում, styling և ամենօրյա խնամք",
      en: "Quick cuts, styling and everyday care",
    },
    icon: "las la-cut",
    services: [
      {
        id: "h1",
        name: { hy: "Տղամարդու դասական խուզում", en: "Classic men's haircut" },
        description: {
          hy: "Մեքենայով և մկրատով, ներառյալ լվացում և սովորական ստայլինգ",
          en: "Clipper + scissor cut, includes wash and basic styling",
        },
        duration: 45,
        price: 7000,
        badge: { hy: "Հաճախ ընտրվող", en: "Popular" },
        masterIds: ["m1", "m2", "m4"],
      },
      {
        id: "h2",
        name: { hy: "Կանացի կարճ խուզում", en: "Women's short haircut" },
        description: {
          hy: "Մինչև ուսերը երկարությամբ մազեր",
          en: "For hair up to shoulder length",
        },
        duration: 60,
        price: 9000,
        badge: { hy: "Նոր look", en: "New look" },
        masterIds: ["m1", "m3"],
      },
      {
        id: "h3",
        name: { hy: "Կանացի երկար խուզում", en: "Women's long haircut" },
        description: {
          hy: "Ուսերից ներքև երկարությամբ մազեր",
          en: "For hair below shoulder length",
        },
        duration: 75,
        price: 12000,
        masterIds: ["m1", "m3"],
      },
      {
        id: "h4",
        name: { hy: "Երեխայի խուզում (մինչև 10 տ․)", en: "Kids haircut (up to 10)" },
        description: {
          hy: "Հատուկ մոտեցում փոքրիկներին",
          en: "Gentle approach for the little ones",
        },
        duration: 30,
        price: 5000,
        masterIds: ["m2", "m4"],
      },
    ],
  },
  {
    id: "color",
    name: { hy: "Մազերի ներկում", en: "Hair coloring" },
    description: {
      hy: "Գույնի թարմացումից մինչև բարդ լուսավորում",
      en: "From color refresh to complex lightening",
    },
    icon: "las la-palette",
    services: [
      {
        id: "c1",
        name: { hy: "Մեկ տոնով ներկում", en: "Single-tone color" },
        description: {
          hy: "Արհեստավարժ ներկ՝ Wella կամ L'Oréal Professional",
          en: "Professional dyes by Wella or L'Oréal Professional",
        },
        duration: 90,
        priceFrom: 18000,
        masterIds: ["m1", "m3"],
      },
      {
        id: "c2",
        name: { hy: "Մելիրովկա / բեյբիլայթս", en: "Highlights / babylights" },
        description: {
          hy: "Բնական անցումներով լուսավորում",
          en: "Subtle natural-looking highlights",
        },
        duration: 150,
        priceFrom: 30000,
        badge: { hy: "Պրեմիում", en: "Premium" },
        masterIds: ["m1", "m3"],
      },
      {
        id: "c3",
        name: { hy: "Արմատների ներկում", en: "Root touch-up" },
        description: {
          hy: "Մինչև 3 սմ արմատների թարմացում",
          en: "Up to 3 cm root refresh",
        },
        duration: 60,
        price: 12000,
        masterIds: ["m1", "m3"],
      },
    ],
  },
  {
    id: "beard",
    name: { hy: "Մորուքի և բեղի խնամք", en: "Beard & shave" },
    description: {
      hy: "Barber ծառայություններ տղամարդկանց համար",
      en: "Barber services for men",
    },
    icon: "las la-air-freshener",
    services: [
      {
        id: "b1",
        name: { hy: "Մորուքի մոդելավորում", en: "Beard trim & shape" },
        description: {
          hy: "Մկրատով և ածելիով, տաք սրբիչ",
          en: "Scissor + razor with hot-towel finish",
        },
        duration: 30,
        price: 4500,
        masterIds: ["m2", "m4"],
      },
      {
        id: "b2",
        name: { hy: "Թաց սափրում (straight razor)", en: "Hot wet shave" },
        description: {
          hy: "Դասական ածելիով, յուղով և տաք սրբիչով",
          en: "Classic straight-razor shave with oil and hot towels",
        },
        duration: 40,
        price: 6000,
        masterIds: ["m2", "m4"],
      },
      {
        id: "b3",
        name: { hy: "Խուզում + մորուք (combo)", en: "Haircut + beard combo" },
        description: {
          hy: "Միանգամից տնտեսում 1500 դրամ",
          en: "Save 1500 AMD vs. booking separately",
        },
        duration: 60,
        price: 10000,
        badge: { hy: "Խնայող combo", en: "Save combo" },
        masterIds: ["m2", "m4"],
      },
    ],
  },
  {
    id: "nails",
    name: { hy: "Մատնահարդարում", en: "Nails" },
    description: {
      hy: "Մաքուր manicure, gel polish և pedicure",
      en: "Clean manicure, gel polish and pedicure",
    },
    icon: "las la-hand-sparkles",
    services: [
      {
        id: "n1",
        name: { hy: "Դասական մատնահարդարում", en: "Classic manicure" },
        description: {
          hy: "Կուտիկուլի մշակում, ձև, ծածկույթ",
          en: "Cuticle care, shaping, base coat",
        },
        duration: 45,
        price: 5000,
        masterIds: ["m5"],
      },
      {
        id: "n2",
        name: { hy: "Գել-լաքի ծածկույթ", en: "Gel polish application" },
        description: {
          hy: "Մինչև 3 շաբաթ կպչուն վերջնահարդարում",
          en: "Up to 3 weeks of long-lasting finish",
        },
        duration: 60,
        price: 8000,
        masterIds: ["m5"],
      },
      {
        id: "n3",
        name: { hy: "Ոտնահարդարում", en: "Pedicure" },
        description: {
          hy: "Կլասիկ ոտնահարդարում + դիմակ",
          en: "Classic pedicure with mask",
        },
        duration: 60,
        price: 9000,
        masterIds: ["m5"],
      },
    ],
  },
  {
    id: "spa",
    name: { hy: "SPA և դիմահարդարում", en: "SPA & makeup" },
    description: {
      hy: "Makeup, brows և միջոցառումների պատրաստում",
      en: "Makeup, brows and event-ready looks",
    },
    icon: "las la-spa",
    services: [
      {
        id: "s1",
        name: { hy: "Դիմահարդարում (օրվա)", en: "Day makeup" },
        description: {
          hy: "Բնական, թեթև տեսք",
          en: "Soft natural look",
        },
        duration: 60,
        price: 12000,
        masterIds: ["m3"],
      },
      {
        id: "s2",
        name: { hy: "Հարսնական դիմահարդարում", en: "Bridal makeup" },
        description: {
          hy: "Փորձնական + միջոցառման օր",
          en: "Trial + event-day application",
        },
        duration: 120,
        price: 30000,
        masterIds: ["m3"],
      },
      {
        id: "s3",
        name: { hy: "Հոնքերի ձևավորում", en: "Brow shaping" },
        description: {
          hy: "Մոմ կամ թել + ներկ",
          en: "Wax or thread + tint",
        },
        duration: 30,
        price: 4000,
        masterIds: ["m3", "m5"],
      },
    ],
  },
];

const SERVICE_BUNDLES = [
  {
    id: "barber-ready",
    name: { hy: "Barber ready", en: "Barber ready" },
    description: {
      hy: "Խուզում + մորուք՝ մեկ այցով, արագ հաստատվող ժամերով",
      en: "Haircut + beard in one visit with fast available slots",
    },
    serviceIds: ["h1", "b1"],
    badge: { hy: "Լավագույնը տղամարդկանց համար", en: "Best for men" },
    saving: 1500,
  },
  {
    id: "event-glow",
    name: { hy: "Event glow", en: "Event glow" },
    description: {
      hy: "Վարսահարդարում + երեկոյան makeup միջոցառումից առաջ",
      en: "Hair styling + evening makeup before an event",
    },
    serviceIds: ["h2", "s1"],
    badge: { hy: "Միջոցառման համար", en: "Event-ready" },
    saving: 2500,
  },
  {
    id: "fresh-nails",
    name: { hy: "Fresh nails", en: "Fresh nails" },
    description: {
      hy: "Manicure + gel polish՝ ամբողջական արդյունքով",
      en: "Manicure + gel polish for a complete finish",
    },
    serviceIds: ["n1", "n2"],
    badge: { hy: "Հաճախ միասին", en: "Booked together" },
    saving: 1000,
  },
];

/* ---------------------------- MASTERS ---------------------------- */
/**
 * `workingDays`: 0=Sunday, 1=Monday, …, 6=Saturday (JS `Date.getDay()`).
 * `workingHours`: { from: "10:00", to: "21:00" } — local time, salon TZ.
 * `serviceIds` is reverse-derived from `service.masterIds` at the bottom of
 * this file. UI components should never read it from a partially-built
 * master record.
 */
const MASTERS = [
  {
    id: "m1",
    name: { hy: "Աննա Մինասյան", en: "Anna Minasyan" },
    title: { hy: "Թոփ-ստիլիստ", en: "Top stylist" },
    bio: {
      hy: "12+ տարվա փորձ, մասնագիտացած է կանացի ստրիժկաների և ներկման մեջ։",
      en: "12+ years of experience, specialised in women's cuts and coloring.",
    },
    photo:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300&h=300&fit=crop&auto=format&q=80",
    rating: 4.95,
    reviewsCount: 312,
    yearsExperience: 12,
    workingDays: [1, 2, 3, 4, 5, 6],
    workingHours: { from: "10:00", to: "21:00" },
  },
  {
    id: "m2",
    name: { hy: "Արամ Հովհաննիսյան", en: "Aram Hovhannisyan" },
    title: { hy: "Բարբեր", en: "Master barber" },
    bio: {
      hy: "Տղամարդկանց ստրիժկաների և մորուքի ձևավորման փորձագետ։",
      en: "Expert in men's cuts and beard sculpting.",
    },
    photo:
      "https://images.unsplash.com/photo-1622286342621-4bd786c2447c?w=300&h=300&fit=crop&auto=format&q=80",
    rating: 4.9,
    reviewsCount: 245,
    yearsExperience: 8,
    workingDays: [2, 3, 4, 5, 6, 0],
    workingHours: { from: "11:00", to: "20:00" },
  },
  {
    id: "m3",
    name: { hy: "Մարիամ Պետրոսյան", en: "Mariam Petrosyan" },
    title: { hy: "Կոլորիստ / դիմահարդար", en: "Colorist / makeup artist" },
    bio: {
      hy: "Հարսնական դիմահարդարման և բարդ գունավորման մասնագետ։",
      en: "Specialist in bridal makeup and complex coloring.",
    },
    photo:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=300&h=300&fit=crop&auto=format&q=80",
    rating: 4.98,
    reviewsCount: 178,
    yearsExperience: 10,
    workingDays: [1, 2, 3, 4, 5, 6],
    workingHours: { from: "10:00", to: "20:00" },
  },
  {
    id: "m4",
    name: { hy: "Դավիթ Սարգսյան", en: "David Sargsyan" },
    title: { hy: "Բարբեր", en: "Barber" },
    bio: {
      hy: "Երիտասարդ, շեշտադրումը՝ ժամանակակից ֆեյդերի վրա։",
      en: "Young talent focused on modern fades.",
    },
    photo:
      "https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=300&h=300&fit=crop&auto=format&q=80",
    rating: 4.85,
    reviewsCount: 96,
    yearsExperience: 4,
    workingDays: [1, 3, 4, 5, 6, 0],
    workingHours: { from: "12:00", to: "21:00" },
  },
  {
    id: "m5",
    name: { hy: "Լիլիթ Ավագյան", en: "Lilit Avagyan" },
    title: { hy: "Մատնահարդար", en: "Nail technician" },
    bio: {
      hy: "Գել-լաքի և դիզայնի մասնագետ։",
      en: "Gel polish and nail art expert.",
    },
    photo:
      "https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=300&h=300&fit=crop&auto=format&q=80",
    rating: 4.92,
    reviewsCount: 204,
    yearsExperience: 6,
    workingDays: [1, 2, 3, 4, 5, 6],
    workingHours: { from: "10:00", to: "20:00" },
  },
];

/* Reverse-index serviceIds onto each master from the SERVICE_CATEGORIES table.
   We do this here (rather than duplicating ids in two places) so the source
   of truth stays the `service.masterIds` arrays above. */
for (const master of MASTERS) {
  master.serviceIds = [];
}
for (const category of SERVICE_CATEGORIES) {
  for (const service of category.services) {
    for (const mid of service.masterIds) {
      const m = MASTERS.find((x) => x.id === mid);
      if (m && !m.serviceIds.includes(service.id)) {
        m.serviceIds.push(service.id);
      }
    }
  }
}

/* ---------------------------- ACCESS HELPERS ---------------------------- */

export function getSalon(slug) {
  return SALONS[slug] || null;
}

export function getServiceCategories() {
  return SERVICE_CATEGORIES;
}

export function getServiceBundles() {
  return SERVICE_BUNDLES.map((bundle) => {
    const services = bundle.serviceIds.map(getServiceById).filter(Boolean);
    const cartServices = services.map((service, index) => {
      if (index !== 0 || !bundle.saving) return service;
      const regular = service.price ?? service.priceFrom ?? 0;
      const discounted = Math.max(regular - bundle.saving, 0);
      return {
        ...service,
        price: service.price != null ? discounted : service.price,
        priceFrom: service.price == null && service.priceFrom != null ? discounted : service.priceFrom,
        bundleId: bundle.id,
      };
    });
    return {
      ...bundle,
      services,
      cartServices,
      duration: services.reduce((sum, service) => sum + (service.duration || 0), 0),
      price: services.reduce(
        (sum, service) => sum + (service.price || service.priceFrom || 0),
        0,
      ),
    };
  });
}

export function getServiceById(id) {
  for (const category of SERVICE_CATEGORIES) {
    const found = category.services.find((s) => s.id === id);
    if (found) return { ...found, categoryId: category.id };
  }
  return null;
}

export function getMasters() {
  return MASTERS;
}

export function getMasterById(id) {
  return MASTERS.find((m) => m.id === id) || null;
}

/**
 * Return the subset of masters that can perform ALL of the given services.
 * When a service has an empty `masterIds`, every master is allowed.
 *
 * Used by the booking flow so the "Choose master" screen only ever shows
 * masters who can actually deliver the full cart. If the cart is empty,
 * returns every master (so the picker is browseable from the start).
 */
export function getMastersForServices(serviceIds) {
  if (!serviceIds || serviceIds.length === 0) return MASTERS;
  return MASTERS.filter((master) => {
    return serviceIds.every((sid) => {
      const svc = getServiceById(sid);
      if (!svc) return false;
      if (!svc.masterIds || svc.masterIds.length === 0) return true;
      return svc.masterIds.includes(master.id);
    });
  });
}

/* ---------------------------- SLOT GENERATION ---------------------------- */
/**
 * Build a 14-day availability calendar for a master.
 *
 * Slots are generated in 15-minute increments inside the master's working
 * window. To make the demo feel realistic, ~30% of slots are pre-blocked
 * with a deterministic hash so the same date always shows the same gaps
 * between renders.
 *
 * Returns: `[{ date: "YYYY-MM-DD", weekday: 0-6, slots: ["10:00", ...] }]`
 *
 * `totalDuration` is the sum of selected service durations (minutes). Slots
 * whose start time would push the appointment past the master's closing
 * time are filtered out client-side. When the backend takes over, this
 * lookahead lives on the server inside `GET /salon-availability`.
 */
export function generateAvailability({ masterId, totalDuration = 30, daysAhead = 14 }) {
  const master = getMasterById(masterId);
  if (!master) return [];

  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let dayOffset = 0; dayOffset < daysAhead; dayOffset += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() + dayOffset);
    const weekday = date.getDay();
    if (!master.workingDays.includes(weekday)) continue;

    const [startH, startM] = master.workingHours.from.split(":").map(Number);
    const [endH, endM] = master.workingHours.to.split(":").map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    const slots = [];
    const step = 15;
    const seedBase = date.getDate() * 31 + (date.getMonth() + 1) * 7 + master.id.charCodeAt(1);

    for (let m = startMinutes; m + totalDuration <= endMinutes; m += step) {
      const slotHour = Math.floor(m / 60);
      const slotMin = m % 60;
      const time = `${String(slotHour).padStart(2, "0")}:${String(slotMin).padStart(2, "0")}`;

      const seed = (seedBase + m) % 100;
      const isBlocked = seed < 30;

      const isPast =
        dayOffset === 0 &&
        (slotHour < today.getHours() ||
          (slotHour === today.getHours() && slotMin <= today.getMinutes() + 15));

      if (!isBlocked && !isPast) slots.push(time);
    }

    if (slots.length > 0) {
      days.push({
        date: formatYMD(date),
        weekday,
        slots,
      });
    }
  }
  return days;
}

/**
 * "Any master" availability — the union of every eligible master's free
 * slots. Each returned slot remembers WHICH master is free at that time, so
 * when the user picks a slot we can automatically assign them.
 *
 * Returns: `[{ date, weekday, slots: [{ time, masterId }] }]`
 */
export function generateAnyMasterAvailability({ serviceIds, totalDuration = 30, daysAhead = 14 }) {
  const eligibleMasters = getMastersForServices(serviceIds);
  if (eligibleMasters.length === 0) return [];

  const merged = new Map();
  for (const master of eligibleMasters) {
    const days = generateAvailability({ masterId: master.id, totalDuration, daysAhead });
    for (const day of days) {
      if (!merged.has(day.date)) {
        merged.set(day.date, { date: day.date, weekday: day.weekday, slots: new Map() });
      }
      const bucket = merged.get(day.date);
      for (const time of day.slots) {
        if (!bucket.slots.has(time)) bucket.slots.set(time, master.id);
      }
    }
  }
  return Array.from(merged.values())
    .map((day) => ({
      date: day.date,
      weekday: day.weekday,
      slots: Array.from(day.slots.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([time, masterId]) => ({ time, masterId })),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

export function formatYMD(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/* ---------------------------- BOOKING CONFIRM ---------------------------- */
/**
 * Fake "create booking" — mirrors the response shape of the future
 * `POST /api/{locale}/salon/bookings` endpoint. We add a 600 ms delay so
 * the success animation feels like a real network round-trip.
 */
export function createMockBooking(payload) {
  return new Promise((resolve) => {
    setTimeout(() => {
      const ref = `GS-${Date.now().toString().slice(-6)}`;
      resolve({
        bookingId: Math.floor(Math.random() * 100000) + 1,
        reference: ref,
        status: "confirmed",
        createdAt: new Date().toISOString(),
        ...payload,
      });
    }, 600);
  });
}
